import React from 'react';
import axios from 'axios';
import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { T, MONO, tint } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useIsMobile } from './poyais-ui/hooks';
import { exportCsv } from './poyais-ui/export';
import { useLang, addTranslations } from './i18n';
import { GrowthStyles, GrowthHero, SectionCard, Segmented, SkeletonPage, ErrorState, Delta, LegendDot, cardStyle, tooltipStyle } from './GrowthUI';

addTranslations({
  'Tahminleme': 'Forecasting',
  'Önümüzdeki {f} ayda seni ne bekliyor?': 'What do the next {f} months look like?',
  'Son {n} ayın trendinden {m} kanal bazında gelir ve sipariş tahmini.': 'Revenue and order forecast by {m} channels, based on the last {n} months of trend.',
  'Geçmiş aylık trendden kanal bazında gelir/sipariş tahmini üretir.': 'Generates a revenue/order forecast from recent monthly trend, broken down by channel.',
  'En İyi': 'Best',
  'Beklenen': 'Expected',
  'En Kötü': 'Worst',
  'En İyi Senaryo': 'Best Case',
  'En Kötü Senaryo': 'Worst Case',
  'Ay': 'Month',
  '3 Ay': '3 Months',
  '6 Ay': '6 Months',
  '12 Ay': '12 Months',
  'Senaryo': 'Scenario',
  'Süre': 'Horizon',
  'Tahmini Gelir': 'Predicted Revenue',
  'Tahmini Sipariş': 'Predicted Orders',
  'Ort. Sipariş Değeri': 'Avg. Order Value',
  'Model Güveni': 'Model Confidence',
  'Ağırlıklı Hareketli Ortalama': 'Weighted Moving Average',
  'öncekine göre': 'vs prev.',
  'güven': 'confidence',
  'sonraki ay': 'next month',
  'Gelir Trendi & Tahmini': 'Revenue Trend & Forecast',
  '{h} geçmiş ay + {n} ay tahmin': '{h} historical months + {n} forecast months',
  'Cari ay hariç': 'Current month excluded',
  'Geçmiş': 'Historical',
  'Tahmin': 'Forecast',
  'Güven Bandı': 'Confidence Band',
  'Bugün': 'Today',
  'Senaryo Karşılaştırması': 'Scenario Comparison',
  'En iyi, beklenen ve en kötü senaryo gelir tahminleri.': 'Best, expected, and worst case revenue projections.',
  'Güven': 'Confidence',
  'tahmin': 'forecast',
  'Kanal Tahminleri': 'Channel Forecasts',
  'Kanal bazlı büyüme oranları ve tahminler. Detay için bir kanala dokun.': 'Per-channel growth rates and predictions. Tap a channel for details.',
  'Son ay': 'Last month',
  'Sonraki ay': 'Next month',
  '/ay': '/mo',
  'Oturum': 'Sessions',
  'Sipariş': 'Orders',
  'Gelir': 'Revenue',
  'DÖ.O': 'CR',
  'OSD': 'AOV',
  'Tahmin verisi yüklenemedi.': 'Could not load forecast data.',
  'Dışa Aktar': 'Export',
  'Gelir payı': 'Revenue share',
});

const API = '/api/v1';

type MonthPoint = {
  month: string; type: 'historical' | 'forecast';
  sessions?: number; orders: number; revenue: number; aov: number; confidence?: number;
};
type ChannelForecast = {
  id: string; label: string; last_revenue: number; next_revenue: number;
  growth_rate_pct: number; confidence: number; months: MonthPoint[];
};
type TrendPoint = { month: string; type: 'historical' | 'forecast'; revenue: number; revenue_low?: number; revenue_high?: number };
type Scenario = { month: string; type: 'historical' | 'forecast'; revenue?: number; best?: number; expected?: number; worst?: number; vs_prev_pct?: number | null; confidence?: number };
type Metric = { value: number; vs_prev_pct: number | null; confidence: number };
type ApiResult = {
  meta: { months_history: number; months_forecast: number; channels_count: number };
  summary: { predicted_revenue: Metric; predicted_orders: Metric; avg_order_value: Metric; model_confidence_avg: number; model_name: string };
  trend: TrendPoint[];
  scenarios: Scenario[];
  channels: ChannelForecast[];
};

type ScenarioKey = 'best' | 'expected' | 'worst';

const money = (v: number) => '₺' + Math.round(v || 0).toLocaleString('tr-TR');
const compactMoney = (v: number) => {
  const a = Math.abs(v || 0);
  if (a >= 1_000_000) return '₺' + (v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 }) + 'M';
  if (a >= 10_000) return '₺' + Math.round(v / 1000).toLocaleString('tr-TR') + 'B';
  return money(v);
};

function monthLabel(m: string, lang: string) {
  const d = new Date(m + 'T00:00:00');
  return d.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', year: '2-digit' });
}

function ChartTooltip({ active, payload, label }: any) {
  const { t, lang } = useLang();
  if (!active || !payload?.length) return null;
  const hist = payload.find((p: any) => p.dataKey === 'historical')?.value;
  const fc = payload.find((p: any) => p.dataKey === 'forecast')?.value;
  const band = payload.find((p: any) => p.dataKey === 'band')?.value as [number, number] | undefined;
  return (
    <div style={tooltipStyle}>
      <div style={{ fontWeight: 800, color: T.text, marginBottom: 4 }}>{monthLabel(label, lang)}</div>
      {hist != null && <div style={{ color: T.textMid, fontWeight: 600 }}>{t('Geçmiş')}: {money(hist)}</div>}
      {fc != null && hist == null && <div style={{ color: T.blueText, fontWeight: 700 }}>{t('Tahmin')}: {money(fc)}</div>}
      {band && band[0] !== band[1] && <div style={{ color: T.textDim, marginTop: 2 }}>{t('Güven Bandı')}: {money(band[0])} – {money(band[1])}</div>}
    </div>
  );
}

function ConfidenceBar({ value, color = T.accent }: { value: number; color?: string }) {
  const { t } = useLang();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: T.trackBg, borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, value))}%`, height: '100%', background: color, borderRadius: 999, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 11, color: T.textDim, fontFamily: MONO, fontWeight: 700, flexShrink: 0 }}>{value}% {t('güven')}</span>
    </div>
  );
}

function Kpi({ icon, color, label, value, delta, confidence, sub, highlight }: {
  icon: React.ReactNode; color: string; label: string; value: string; delta?: number | null; confidence?: number; sub?: React.ReactNode; highlight?: boolean;
}) {
  const { t } = useLang();
  return (
    <div className="gr-hover" style={{
      ...cardStyle, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0,
      ...(highlight ? { background: `linear-gradient(140deg, ${tint(color, 14)} 0%, var(--py-card) 75%)`, borderColor: tint(color, 40) } : {}),
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 30, height: 30, borderRadius: 9, background: tint(color, 15), color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid, lineHeight: 1.25 }}>{label}</span>
      </div>
      <span style={{ fontSize: 'clamp(19px, 2.2vw, 25px)', fontWeight: 800, color: T.text, fontFamily: MONO, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
      {delta !== undefined && <Delta value={delta} suffix={t('öncekine göre')} />}
      {sub}
      {confidence !== undefined && <ConfidenceBar value={confidence} color={color} />}
    </div>
  );
}

function ChannelCard({ ch, maxRevenue, isMobile }: { ch: ChannelForecast; maxRevenue: number; isMobile: boolean }) {
  const { t, lang } = useLang();
  const [expanded, setExpanded] = React.useState(false);
  const up = ch.growth_rate_pct >= 0;
  const share = maxRevenue > 0 ? ch.next_revenue / maxRevenue * 100 : 0;

  const handleExport = (e: React.MouseEvent) => {
    e.stopPropagation();
    exportCsv(`tahminleme-${ch.id}.csv`, ch.months, [
      { key: 'month', label: t('Ay') },
      { key: 'sessions', label: t('Oturum') },
      { key: 'orders', label: t('Sipariş') },
      { key: 'aov', label: t('OSD') },
      { key: 'revenue', label: t('Gelir') },
    ]);
  };

  return (
    <div className="gr-hover" style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
      <button onClick={() => setExpanded(!expanded)} style={{ display: 'block', width: '100%', padding: isMobile ? 14 : '14px 18px', background: 'none', border: 'none',
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: T.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.label}</span>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: up ? T.greenText : T.redText, background: up ? T.greenTint : T.redTint, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>
            {up ? '↑' : '↓'} {Math.abs(ch.growth_rate_pct).toFixed(1)}%{t('/ay')}
          </span>
          <span style={{ color: T.textDim, display: 'flex', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><Icon.chevron size={15} /></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, color: T.textDim, fontWeight: 700 }}>{t('Son ay')}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textMid, fontFamily: MONO }}>{money(ch.last_revenue)}</div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, color: T.textDim, fontWeight: 700 }}>{t('Sonraki ay')}</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: MONO }}>{money(ch.next_revenue)}</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 800, color: T.amberDeepText, background: T.amberTint, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>{ch.confidence}% {t('güven')}</span>
        </div>
        <div style={{ height: 5, background: T.trackBg, borderRadius: 999, overflow: 'hidden', marginTop: 10 }}>
          <div style={{ width: `${share}%`, height: '100%', background: up ? T.green : T.red, opacity: 0.75, borderRadius: 999 }} />
        </div>
      </button>

      {expanded && (
        <div className="gr-fade" style={{ borderTop: `1px solid ${T.divider}`, padding: isMobile ? '10px 14px 14px' : '12px 18px 16px', background: T.subtleBg }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={handleExport}
              style={{ padding: '6px 12px', borderRadius: 9, border: `1px solid ${T.borderMid}`, background: T.surface, fontSize: 11.5, fontWeight: 700, color: T.textMidAlt, cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon.download size={13} /> {t('Dışa Aktar')}
            </button>
          </div>
          <div className="gr-thin" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 460 }}>
              <thead>
                <tr style={{ color: T.textDim, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {[t('Ay'), t('Oturum'), t('DÖ.O'), t('Sipariş'), t('OSD'), t('Gelir')].map((h, i) => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 800 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ch.months.map(m => {
                  const cr = m.sessions ? (m.orders / m.sessions * 100) : null;
                  const isForecast = m.type === 'forecast';
                  return (
                    <tr key={m.month} style={{ borderTop: `1px solid ${T.divider}`, background: isForecast ? tint(T.blue, 6) : 'transparent' }}>
                      <td style={{ padding: '7px 8px', color: isForecast ? T.blueText : T.text, fontWeight: isForecast ? 800 : 600, whiteSpace: 'nowrap' }}>
                        {monthLabel(m.month, lang)} {isForecast && <span style={{ fontSize: 10, color: T.textDim, fontWeight: 600 }}>· {t('tahmin')}</span>}
                      </td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: T.textMid, fontFamily: MONO }}>{m.sessions != null ? m.sessions.toLocaleString('tr-TR') : '—'}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: T.textMid, fontFamily: MONO }}>{cr != null ? `%${cr.toFixed(2)}` : '—'}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: T.textMid, fontFamily: MONO }}>{m.orders.toLocaleString('tr-TR')}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: T.textMid, fontFamily: MONO }}>{money(m.aov)}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: T.text, fontFamily: MONO, fontWeight: 800 }}>{money(m.revenue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function GrowthForecast({ clientId }: { clientId: number }) {
  const { t, lang } = useLang();
  const isMobile = useIsMobile();
  const [monthsForecast, setMonthsForecast] = React.useState(3);
  const [scenario, setScenario] = React.useState<ScenarioKey>('expected');
  const [data, setData] = React.useState<ApiResult | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/growth/forecast?client_id=${clientId}&months_forecast=${monthsForecast}`)
      .then(res => setData(res.data))
      .catch(err => setError(err?.response?.data?.detail || t('Tahmin verisi yüklenemedi.')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, monthsForecast]);

  const chartData = React.useMemo(() => {
    if (!data) return [];
    const merged = data.trend.map(p => ({
      month: p.month,
      historical: p.type === 'historical' ? p.revenue : null,
      forecast: p.type === 'forecast' ? p.revenue : null,
      band: p.type === 'forecast' && p.revenue_low != null && p.revenue_high != null ? [p.revenue_low, p.revenue_high] : null,
    }));
    const firstForecastIdx = merged.findIndex(p => p.forecast !== null);
    if (firstForecastIdx > 0) {
      const bridge = merged[firstForecastIdx - 1];
      bridge.forecast = bridge.historical;
      bridge.band = [bridge.historical as number, bridge.historical as number];
    }
    return merged;
  }, [data]);
  const lastHistMonth = data ? [...data.trend].reverse().find(p => p.type === 'historical')?.month : undefined;

  const scenarioMetric = (m: Metric | undefined, s: Scenario | undefined) => {
    if (!m) return 0;
    if (!s || scenario === 'expected') return m.value;
    const base = s.expected || m.value;
    const factor = scenario === 'best' ? (s.best || base) / base : (s.worst || base) / base;
    return m.value * factor;
  };
  const firstForecastScenario = data?.scenarios.find(s => s.type === 'forecast');
  const scenarioColor: Record<ScenarioKey, string> = { best: T.greenText, expected: T.blueText, worst: T.redText };
  const maxChannelRevenue = Math.max(0, ...(data?.channels || []).map(c => c.next_revenue));

  const controls = (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 10, alignItems: isMobile ? 'stretch' : 'center' }}>
      <Segmented<ScenarioKey> full={isMobile} value={scenario} onChange={setScenario} options={[
        { key: 'best', label: t('En İyi'), color: T.greenText },
        { key: 'expected', label: t('Beklenen'), color: T.blueText },
        { key: 'worst', label: t('En Kötü'), color: T.redText },
      ]} />
      <Segmented<number> full={isMobile} value={monthsForecast} onChange={setMonthsForecast} options={[
        { key: 3, label: t('3 Ay') }, { key: 6, label: t('6 Ay') }, { key: 12, label: t('12 Ay') },
      ]} />
    </div>
  );

  return (
    <div>
      <GrowthStyles />
      <GrowthHero isMobile={isMobile} color={T.purple} icon={<Icon.chart size={24} sw={2.1} />}
        title={t('Önümüzdeki {f} ayda seni ne bekliyor?').replace('{f}', String(monthsForecast))}
        desc={data
          ? t('Son {n} ayın trendinden {m} kanal bazında gelir ve sipariş tahmini.').replace('{n}', String(data.meta.months_history)).replace('{m}', String(data.meta.channels_count))
          : t('Geçmiş aylık trendden kanal bazında gelir/sipariş tahmini üretir.')}
        right={!error ? controls : undefined} />

      {loading && <SkeletonPage isMobile={isMobile} />}
      {!loading && error && <ErrorState error={error} />}

      {!loading && !error && data && (
        <div className="gr-fade">
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))', gap: isMobile ? 10 : 12, marginBottom: 14 }}>
            <Kpi icon={<Icon.chart size={15} sw={2.1} />} color={T.blue} highlight
              label={t('Tahmini Gelir')}
              value={money(scenarioMetric(data.summary.predicted_revenue, firstForecastScenario))}
              delta={data.summary.predicted_revenue.vs_prev_pct}
              confidence={data.summary.predicted_revenue.confidence} />
            <Kpi icon={<Icon.checklist size={15} />} color={T.green}
              label={t('Tahmini Sipariş')}
              value={Math.round(scenarioMetric(data.summary.predicted_orders, firstForecastScenario)).toLocaleString('tr-TR')}
              delta={data.summary.predicted_orders.vs_prev_pct}
              confidence={data.summary.predicted_orders.confidence} />
            <Kpi icon={<Icon.invoice size={15} />} color={T.amber}
              label={t('Ort. Sipariş Değeri')}
              value={money(data.summary.avg_order_value.value)}
              delta={data.summary.avg_order_value.vs_prev_pct}
              confidence={data.summary.avg_order_value.confidence} />
            <Kpi icon={<Icon.target size={15} />} color={T.purple}
              label={t('Model Güveni')}
              value={`${data.summary.model_confidence_avg}%`}
              sub={<span style={{ fontSize: 11.5, color: T.textDim, fontWeight: 600 }}>{t('Ağırlıklı Hareketli Ortalama')}</span>}
              confidence={undefined} />
          </div>

          <SectionCard style={{ marginBottom: 14 }} pad={isMobile ? 14 : 18}
            title={t('Gelir Trendi & Tahmini')}
            sub={<>{t('{h} geçmiş ay + {n} ay tahmin').replace('{h}', String(data.meta.months_history)).replace('{n}', String(data.meta.months_forecast))} · <span style={{ color: T.amberDeepText, fontWeight: 700 }}>{t('Cari ay hariç')}</span></>}
            right={<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <LegendDot line color={T.text} label={t('Geçmiş')} />
              <LegendDot line dashed color={T.blue} label={t('Tahmin')} />
              <LegendDot color={tint(T.blue, 30)} label={t('Güven Bandı')} />
            </div>}>
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 340}>
              <ComposedChart data={chartData} margin={{ top: 14, right: isMobile ? 4 : 16, bottom: 0, left: isMobile ? -12 : 0 }}>
                <defs>
                  <linearGradient id="grBand" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--py-blue)" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="var(--py-blue)" stopOpacity={0.06} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.divider as any} vertical={false} />
                <XAxis dataKey="month" tickFormatter={(m) => monthLabel(m, lang)} tick={{ fontSize: 10.5, fill: T.textDim as any }} axisLine={false} tickLine={false} minTickGap={10} />
                <YAxis tickFormatter={(v) => compactMoney(v)} tick={{ fontSize: 10.5, fill: T.textDim as any }} axisLine={false} tickLine={false} width={isMobile ? 50 : 62} />
                <Tooltip content={<ChartTooltip />} />
                {lastHistMonth && <ReferenceLine x={lastHistMonth} stroke={T.borderMid as any} strokeDasharray="3 3" label={{ value: t('Tahmin') + ' →', position: 'insideTopRight', fontSize: 10.5, fontWeight: 700, fill: T.blueText as any }} />}
                <Area dataKey="band" stroke="none" fill="url(#grBand)" isAnimationActive={false} />
                <Line dataKey="historical" name={t('Geçmiş')} stroke={T.text as any} strokeWidth={2.5} dot={{ r: 3, strokeWidth: 0, fill: T.text as any }} connectNulls={false} isAnimationActive={false} />
                <Line dataKey="forecast" name={t('Tahmin')} stroke={T.blue as any} strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3.5, fill: T.blue as any, strokeWidth: 0 }} connectNulls isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </SectionCard>

          <SectionCard style={{ marginBottom: 14 }} pad={isMobile ? 14 : 18}
            title={t('Senaryo Karşılaştırması')} sub={t('En iyi, beklenen ve en kötü senaryo gelir tahminleri.')}>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.scenarios.filter(s => s.type === 'forecast').map(s => (
                  <div key={s.month} style={{ background: T.subtleBg, borderRadius: 12, padding: '11px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{monthLabel(s.month, lang)}</span>
                      {s.confidence != null && <span style={{ fontSize: 11, fontWeight: 800, color: T.amberDeepText, background: T.amberTint, borderRadius: 999, padding: '2px 9px' }}>{s.confidence}% {t('güven')}</span>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }}>
                      {(['best', 'expected', 'worst'] as ScenarioKey[]).map(k => (
                        <div key={k} style={{ background: T.surface, borderRadius: 9, padding: '7px 8px', border: `1px solid ${scenario === k ? tint(scenarioColor[k], 50) : 'transparent'}` }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: scenarioColor[k] }}>{t(k === 'best' ? 'En İyi' : k === 'expected' ? 'Beklenen' : 'En Kötü')}</div>
                          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text, fontFamily: MONO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{compactMoney((s as any)[k] || 0)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="gr-thin" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520 }}>
                  <thead>
                    <tr style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', color: T.textDim, fontWeight: 800, borderBottom: `1px solid ${T.border}` }}>{t('Ay')}</th>
                      {(['best', 'expected', 'worst'] as ScenarioKey[]).map(k => (
                        <th key={k} style={{ padding: '8px 10px', textAlign: 'right', color: scenarioColor[k], fontWeight: 800, borderBottom: `1px solid ${T.border}`,
                          background: scenario === k ? tint(scenarioColor[k], 7) : 'transparent', borderRadius: '8px 8px 0 0' }}>
                          {t(k === 'best' ? 'En İyi Senaryo' : k === 'expected' ? 'Beklenen' : 'En Kötü Senaryo')}
                        </th>
                      ))}
                      <th style={{ padding: '8px 10px', textAlign: 'right', color: T.textDim, fontWeight: 800, borderBottom: `1px solid ${T.border}` }}>{t('Güven')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.scenarios.map(s => {
                      const isForecast = s.type === 'forecast';
                      const cell = (k: ScenarioKey, v: React.ReactNode) => (
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: MONO, background: scenario === k ? tint(scenarioColor[k], 7) : 'transparent',
                          color: isForecast ? scenarioColor[k] : T.text, fontWeight: isForecast ? 800 : 600 }}>{v}</td>
                      );
                      return (
                        <tr key={s.month} className="gr-row" style={{ borderBottom: `1px solid ${T.divider}` }}>
                          <td style={{ padding: '9px 10px', color: isForecast ? T.blueText : T.text, fontWeight: isForecast ? 800 : 600, whiteSpace: 'nowrap' }}>
                            {monthLabel(s.month, lang)} {isForecast && <span style={{ fontSize: 10, color: T.textDim, fontWeight: 600 }}>· {t('tahmin')}</span>}
                          </td>
                          {cell('best', isForecast ? money(s.best!) : '')}
                          {cell('expected', <>
                            {money(isForecast ? s.expected! : s.revenue!)}
                            {s.vs_prev_pct != null && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: s.vs_prev_pct >= 0 ? T.greenText : T.redText }}>{s.vs_prev_pct >= 0 ? '↑' : '↓'} {Math.abs(s.vs_prev_pct).toFixed(1)}%</span>}
                          </>)}
                          {cell('worst', isForecast ? money(s.worst!) : '')}
                          <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                            {s.confidence != null && <span style={{ fontSize: 11, fontWeight: 800, color: T.amberDeepText, background: T.amberTint, borderRadius: 999, padding: '2px 9px' }}>{s.confidence}%</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text, letterSpacing: '-0.01em' }}>{t('Kanal Tahminleri')}</div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>{t('Kanal bazlı büyüme oranları ve tahminler. Detay için bir kanala dokun.')}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12, alignItems: 'start' }}>
            {data.channels.map(ch => <ChannelCard key={ch.id} ch={ch} maxRevenue={maxChannelRevenue} isMobile={isMobile} />)}
          </div>
        </div>
      )}
    </div>
  );
}
